/**
 * Shared domain types for the Trading Card Vision System (TCVS).
 *
 * These mirror the data-model tables in `.kiro/specs/trading-card-vision-system/design.md`
 * (Card_Record, Intake_Session, Capture_Batch, Device registry, Training_Example) and
 * the Vision_Service response shapes from the same document. They are the single
 * source of truth for cross-package payload shapes (API <-> SPA <-> firmware).
 *
 * Validates: Requirements 2.5, 3.1, 4.1, 4.3, 6.1, 12.10
 */

import type { GradeLabel } from './grade-label';
import type { IntakeSessionState } from './intake-session';
import type { ChecklistNaturalKey } from './checklist-key';
import type { CardPresentation } from './card-presentation';
import type { ReviewChecklistSelection } from './batch-identification-grid';

// ---------------------------------------------------------------------------
// Card identity + grading primitives
// ---------------------------------------------------------------------------

/** A canonical card identity. Mirrors design.md `Card_Record.identity`.
 *  `category` is the card's franchise/product line — a TCG game (e.g. Pokémon),
 *  a sport (e.g. Baseball), or any other card category.
 *
 *  The four core fields are always present. The remaining fields are optional,
 *  additive listing-service metadata imported from the external Service_CSV
 *  (`csv-identity-import`). When importing, the CSV's `card_number` maps to
 *  `number` and `player` maps to `name`; the rest map by identical name. Absent
 *  optional fields are omitted (never `''`). */
export interface CardIdentity {
  category: string;
  set: string;
  /** CSV `card_number`. */
  number: string;
  /** CSV `player` (sports) or card name (TCG). */
  name: string;
  // Additive, optional listing fields (csv-identity-import):
  year?: string;
  brand?: string;
  subset?: string;
  attributes?: string;
  /** Operator-confirmed rookie-card flag used by listing-copy assembly. */
  rookie?: boolean;
  /** Operator-confirmed autograph flag, independent of free-text attributes. */
  autograph?: boolean;
  /** Operator-confirmed memorabilia/relic flag, independent of free-text attributes. */
  memorabilia?: boolean;
  /** The physical card is serial numbered even when its run size is not yet known. */
  serialNumbered?: boolean;
  team?: string;
  /** Printed card language selected or confirmed during review. */
  language?: string;
  /** checklist: the full CARD SET string, verbatim (Parallel id). */
  variation?: string;
  /**
   * Additive optional print-run size (checklist-reference). A positive integer
   * in `[1, 1,000,000]` denoting the serial run size (`1` = a 1/1; `10` =
   * numbered /10). Absent ⇒ the card is unnumbered/base; NEVER stored as
   * `0`/`null`/`''` (Req 1.2, 1.3). Every existing `CardIdentity` literal/record
   * that omits it stays valid and compiles without modification (Req 1.5).
   */
  serialTo?: number;
  /**
   * Operator-observed serial numerator for this physical card. This is only
   * valid when `serialTo` is also present and must be within that print run.
   */
  serialNumber?: number;
}

/** Operator-reviewed price intended for the card's eventual marketplace listing. */
export interface CardListingPrice {
  /** Integer minor units avoid floating-point currency errors (for USD, cents). */
  amountMinor: number;
  currency: 'USD';
  reviewedAt: string;
  reviewedBy: string;
  /** Search terms used while researching comparable listings. */
  researchQuery?: string;
  /** Concise operator rationale or comparable-listing notes. */
  notes?: string;
}

/** Authoritative source ownership attached to an imported checklist roster row. */
export interface ChecklistSourceReference {
  sourceSystem: string;
  sourceTargetId: string;
  sourceItemId: string;
  /** Exact source-schema subset/parallel definition ID for this roster row. */
  sourceSubsetId?: string;
  /** Identifies which source-schema definition collection owns `sourceSubsetId`. */
  sourceDefinitionType?: 'SUBSET' | 'PARALLEL';
  snapshotFingerprint: string;
  sourceFamilyId?: string;
}

/**
 * Exact imported checklist record chosen for a card during review. This is
 * source lineage only; it does not make the card identity authoritative.
 */
export interface ChecklistSourceSelection extends ChecklistSourceReference {
  checklistId: string;
  naturalKey: ChecklistNaturalKey;
  subset: string;
  parallel?: {
    definitionId: string;
    name: string;
    sourceSubsetId: string;
    snapshotFingerprint: string;
  };
}

/**
 * Durable card-to-checklist source lineage. `status` distinguishes a
 * non-authoritative resolver suggestion or saved draft from a human-verified
 * identity.
 */
export interface CardChecklistSourceLineage extends ChecklistSourceSelection {
  status: 'SUGGESTED' | 'DRAFT' | 'VERIFIED';
  linkedAt: string;
  linkedBy: string;
}

/** Audited reviewer-entered identity used only when no imported checklist fits. */
export interface ChecklistManualSourceEvidence {
  selection: ChecklistSourceSelection;
  unresolvedClassification: import('./checklist-unresolved').ChecklistUnresolvedClassification;
  identity: CardIdentity;
}

export interface CardManualIdentityOverride {
  /** Source evidence only; manual completion is not verified checklist lineage. */
  sourceEvidence?: ChecklistManualSourceEvidence;
  status: 'DRAFT' | 'VERIFIED';
  reason: string;
  reviewedAt: string;
  reviewedBy: string;
}

/** Serial-run (`serialTo`) bounds shared across the checklist-reference feature.
 *  The TYPE bound on `CardIdentity.serialTo` is `[SERIAL_TO_MIN, SERIAL_TO_TYPE_MAX]`
 *  (Req 1.2); checklist ingest only ever writes the narrower
 *  `[SERIAL_TO_MIN, SERIAL_TO_INGEST_MAX]` accepted SEQUENCE range (Req 4.5). */
export const SERIAL_TO_MIN = 1;
/** Upper bound of the `CardIdentity.serialTo` TYPE domain (Req 1.2). */
export const SERIAL_TO_TYPE_MAX = 1_000_000;
/** Upper bound of the checklist SEQUENCE value accepted at ingest (Req 4.5). */
export const SERIAL_TO_INGEST_MAX = 999_999;

/** Validate the optional observed serial and its checklist/manual run size. */
export function cardIdentitySerialIsValid(identity: Partial<CardIdentity>): boolean {
  if (
    identity.serialTo !== undefined &&
    (!Number.isInteger(identity.serialTo) ||
      identity.serialTo < SERIAL_TO_MIN ||
      identity.serialTo > SERIAL_TO_TYPE_MAX)
  ) {
    return false;
  }
  if (identity.serialNumber === undefined) return true;
  return (
    Number.isInteger(identity.serialNumber) &&
    identity.serialNumber >= SERIAL_TO_MIN &&
    identity.serialTo !== undefined &&
    identity.serialNumber <= identity.serialTo
  );
}

/**
 * Maximum byte size for a checklist workbook upload (checklist-reference,
 * Req 6.1/6.3). Reuses the platform-wide 20 MB upload cap (`product.md`:
 * "upload size cap is 20 MB") as a single shared constant rather than a
 * per-call-site literal (steering: caps live in `@tcvs/shared`). Enforced at
 * S3 via the presigned-POST `content-length-range` policy for the
 * `checklist-uploads/` prefix.
 */
export const CHECKLIST_UPLOAD_SIZE_CAP = 20 * 1024 * 1024;

/**
 * The four condition sub-scores produced by the Grading_Model.
 * Each score is an integer in [1, 10] (Requirement 4.1).
 */
export interface SubScores {
  centering: number;
  corners: number;
  edges: number;
  surface: number;
}

/**
 * A defect region returned by the Grading_Model when `corners <= 4` or
 * `surface <= 4` (Requirement 4.4). The bounding box is `[x, y, w, h]` in
 * image coordinates.
 */
export interface Defect {
  type: string;
  bbox: readonly [number, number, number, number];
}

// ---------------------------------------------------------------------------
// Identification response (discriminated union, top-3 candidates)
// ---------------------------------------------------------------------------

/** A single identification candidate with its similarity-derived confidence. */
export interface IdentificationCandidate {
  identity: CardIdentity;
  confidence: number;
}

/**
 * The top-3 candidate list returned by the Identification_Model. Always has
 * exactly three entries for both `known` and `unknown` responses (Requirement 3.2,
 * design.md "Identification Lambda").
 */
export type IdentificationCandidates = readonly [
  IdentificationCandidate,
  IdentificationCandidate,
  IdentificationCandidate,
];

/** Identification result when the top-1 score is at or above the 0.50 threshold. */
export interface KnownIdentification {
  status: 'known';
  identity: CardIdentity;
  /** Top-1 confidence; always >= 0.50 by construction. */
  confidence: number;
  candidates: IdentificationCandidates;
}

/** Identification result when no candidate clears the 0.50 threshold. */
export interface UnknownIdentification {
  status: 'unknown';
  identity: null;
  /** Top-1 confidence; always < 0.50 by construction. */
  confidence: number;
  candidates: IdentificationCandidates;
}

export type IdentificationResponse = KnownIdentification | UnknownIdentification;

// ---------------------------------------------------------------------------
// Grading response
// ---------------------------------------------------------------------------

/**
 * The full grading payload returned by the Vision_Service. Mirrors the API
 * shape under "Key request / response shapes" in design.md.
 *
 * `defects` is empty unless `subScores.corners <= 4` or `subScores.surface <= 4`
 * (Requirement 4.4).
 */
export interface GradingResponse {
  subScores: SubScores;
  /** Overall grade, integer in [1, 10] (Requirement 4.2). */
  grade: number;
  /** Human-readable label derived from `grade` via the Grade Label Table. */
  label: GradeLabel;
  defects: readonly Defect[];
}

// ---------------------------------------------------------------------------
// Card_Record (table: tcvs-cards)
// ---------------------------------------------------------------------------

/** Origin of a Card_Record. */
export type CardRecordSource = 'browser' | 'device' | 'marketplace';

/**
 * Verification state of a Card_Record's identity (physical-first-card-tracking
 * Requirement 1.2). Exactly one of `UNIDENTIFIED` (no human-verified identity)
 * or `IDENTIFIED` (a human-verified authoritative `CardIdentity` is set).
 */
export type IdentityStatus = 'UNIDENTIFIED' | 'IDENTIFIED';

/**
 * Verification state of a Card_Record's grade (physical-first-card-tracking
 * Requirement 1.3). Exactly one of `UNVERIFIED_GRADE` (no human-verified grade)
 * or `VERIFIED_GRADE` (a human-verified authoritative grade is set).
 */
export type GradeStatus = 'UNVERIFIED_GRADE' | 'VERIFIED_GRADE';

/**
 * Operator image-QA state for captured card evidence. This is deliberately
 * separate from identity/grade review: it answers only whether the captured
 * images are acceptable for downstream export/SKU write-back.
 */
export type ImageQualityStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED';

/**
 * Optional, non-authoritative image-only review outcome. This records that an
 * operator inspected the images without changing identity or the authoritative
 * image-quality gate used by downstream workflows.
 */
export type PreliminaryImageReviewStatus = 'APPROVED' | 'ISSUE';

export interface PreliminaryImageReview {
  status: PreliminaryImageReviewStatus;
  reviewedAt: string;
  reviewedBy: string;
}

/**
 * How a Card_Record came into existence (physical-first-card-tracking
 * Requirement 1.5). `device-upload` marks a card created at device Upload time
 * (UNIDENTIFIED/UNVERIFIED_GRADE); `operator-confirm` marks a card materialized
 * via the legacy operator confirm/correct path; `operator-upload` marks a
 * manually uploaded front/back pair placed directly into inventory.
 */
export type CardRecordOrigin =
  | 'device-upload'
  | 'operator-confirm'
  | 'operator-upload'
  | 'marketplace-import';

/**
 * Retained, non-authoritative ML output kept on a Card_Record
 * (physical-first-card-tracking Requirement 2). A `CardSuggestion` is a hint a
 * human MAY accept or correct during optional Review; it is never the card's
 * authoritative identity or grade.
 */
export interface CardSuggestion {
  identification: IdentificationResponse;
  grading: GradingResponse;
}

/**
 * Non-authoritative observations supplied by the Capture_Device operator.
 * These hints may prefill Review controls, but never make identity fields
 * authoritative without an explicit reviewer verification.
 */
export interface CardCaptureHints {
  /** The physical card appears to carry an individually numbered serial stamp. */
  serialNumbered?: boolean;
  /** The physical card is thicker than a standard card and may require adjusted shipping. */
  thickCard?: boolean;
}

/**
 * Which face of a physical card an image depicts (two-sided-card-record
 * Requirement 2, Glossary "Side"). Exactly one of `front` or `back`.
 */
export type Side = 'front' | 'back';

/**
 * Side-labeled image references on a Card_Record (two-sided-card-record
 * Requirements 1.2, 2.1, 2.2). At most one Front_Image and at most one
 * Back_Image (Requirement 2.4). Each value is an S3 object key. A one-sided
 * card has exactly one of these present (Requirements 5.1, 5.2).
 */
export interface CardImages {
  front?: string;
  back?: string;
}

/** One superseded image file of a card side. */
export interface CardImageHistoryEntry {
  /** The S3 key the side pointed at before the replacement; the object is kept. */
  key: string;
  versionId?: string;
  etag?: string;
  replacedAt: string;
  replacedBy?: string;
  /** The key that replaced it. */
  replacementKey: string;
  reason: 'AUTO_ORIENTATION' | 'MANUAL_REPLACEMENT';
  /** Server-side transform for AUTO_ORIENTATION: clockwise quarter turns + clockwise deskew degrees. */
  transform?: { quarterTurns: 0 | 1 | 2 | 3; deskewDegrees: number };
  /** sha256 of source key/version/etag + transform; the same correction is never applied twice. */
  transformFingerprint?: string;
  /** Batch import run that carried the correction. */
  importId?: string;
}
export type CardImageHistory = Partial<Record<'front' | 'back', CardImageHistoryEntry[]>>;

/**
 * A device-captured front/back pair awaiting an authenticated Operator decision.
 * The Capture_Device may create this candidate, but it never changes the
 * Card_Record's canonical `images` references directly.
 */
export interface PendingImageReplacement {
  replacementId: string;
  images: Required<CardImages>;
  submittedAt: string;
  deviceId: string;
  targetCardRef: string;
}

// ---------------------------------------------------------------------------
// OCR metadata (additive, non-authoritative — card-ocr-metadata)
// ---------------------------------------------------------------------------

/**
 * A single recognized line of text within an OCR_Side_Result (card-ocr-metadata
 * Requirement 3.2): the recognized string and its recognition confidence in [0, 1].
 */
export interface OcrTextLine {
  text: string;
  /** Recognition confidence in [0, 1]. */
  confidence: number;
}

/**
 * OCR output for a single Side (`front` or `back`) (card-ocr-metadata Req 3.1):
 * the recognized lines, an aggregate text string for the Side, and an aggregate
 * confidence for the Side. `lines` is empty when no readable text was found on a
 * present Side (Requirement 2.5) — the Side is still recorded.
 */
export interface OcrSideResult {
  text: string;
  lines: OcrTextLine[];
  /** Aggregate confidence in [0, 1]. */
  confidence: number;
}

/**
 * Additive, NON-AUTHORITATIVE OCR text block on a Card_Record (card-ocr-metadata
 * Requirement 3). Holds a per-side OCR_Side_Result for whichever Sides were
 * processed, plus provenance. Like the ML `suggestion` and the `csvSuggestion`,
 * it is a machine-read hint a human or a later identification step MAY use; it is
 * NEVER the card's authoritative identity or grade and NEVER sets/implies
 * identityStatus/gradeStatus (Requirement 3.4, 3.5, 9.2). At least one of `front`
 * or `back` is present when the block exists.
 */
export interface OcrMetadata {
  front?: OcrSideResult;
  back?: OcrSideResult;
  /** OCR engine identifier, e.g. `paddleocr@<version>` (Requirement 3.3). */
  engine: string;
  /** ISO 8601 completion timestamp (Requirement 3.3). */
  completedAt: string;
}

/**
 * Bounded visual observations extracted from the current card images only when
 * deterministic OCR/title/CSV evidence cannot resolve an imported checklist.
 * These fields are evidence for the existing deterministic matcher, never an
 * identity assignment or reviewer confirmation.
 */
export interface ChecklistImageEvidenceFields {
  year?: string;
  manufacturer?: string;
  product?: string;
  cardNumber?: string;
  name?: string;
  players?: string[];
  team?: string;
  league?: string;
  franchise?: string;
  subset?: string;
  parallel?: string;
  language?: string;
  visibleMarks?: string[];
}

/** Cached visual evidence tied to the exact front/back object keys. */
export interface ChecklistImageEvidence {
  version: string;
  model: string;
  imageFingerprint: string;
  generatedAt: string;
  fields: ChecklistImageEvidenceFields;
  inputTokens?: number;
  outputTokens?: number;
}

/**
 * Persisted Card_Record. Mirrors the `tcvs-cards` table in design.md > Data Models.
 * `gradeLabel` is derived from `grade` via the Grade Label Table.
 *
 * Under physical-first-card-tracking, a Card_Record is created at device Upload
 * time as `UNIDENTIFIED`/`UNVERIFIED_GRADE` with no authoritative identity or
 * grade — the ML output is retained only as a non-authoritative `suggestion`.
 * Authoritative `identity`/`grade` become present only through optional operator
 * Review. Placement attributes (owned by Storage_Service) remain orthogonal.
 */
export interface CardRecord {
  buyerReturnHoldId?: string;
  cardId: string;

  /**
   * Set when an administrator retires a card instead of hard-deleting it.
   * Retired cards remain recoverable but are excluded from active inventory,
   * review, and SKU write-back workflows.
   */
  retiredAt?: string;
  retiredBy?: string;
  retiredReason?: string;

  /**
   * Human-friendly short code (short-card-code feature). Additive and optional:
   * present on cards created on/after this feature, absent on Legacy_Cards
   * created before it. When present, a non-empty 1–64 char string in
   * Canonical_Form (Req 2.1, 2.5). Stored as an attribute distinct from
   * `cardId` and never re-keys, migrates, or replaces `cardId` (Req 2.2, 2.3,
   * 2.4, 11).
   */
  shortCode?: string;

  /**
   * External listing-service SKU (sku-write-back feature). Additive and
   * optional: set when an operator writes back the SKU the external service
   * assigned to this card after a run is processed; absent otherwise. An opaque
   * non-empty string. Stored as an attribute distinct from `cardId` /
   * `shortCode` and never re-keys, migrates, or replaces them, nor affects
   * identity, grade, placement, or image attributes (sku-write-back Req 1.1,
   * 1.2).
   */
  sku?: string;
  /**
   * ISO 8601 timestamp of the latest SKU write-back for this card. Additive and
   * optional; absent on cards SKU'd before this timestamp existed.
   */
  skuUpdatedAt?: string;

  /** Reviewed intended listing price; independent of identity, grade, and SKU. */
  listingPrice?: CardListingPrice;

  /**
   * Approximate 1-based position of this card within its uploaded run/batch
   * (sku-write-back). Captured opportunistically at SKU write-back time from the
   * `upload/` folder's ordering — it is a best-effort picking hint ("~card N in
   * the batch"), NOT a guaranteed-accurate location. Additive and optional;
   * absent on cards that never went through write-back. Never re-keys or affects
   * identity/grade/placement.
   */
  batchIndex?: number;

  /**
   * Current Storage_Batch membership while the card is stored. Storage owns
   * this additive attribute; it remains independent of identity and pricing.
   */
  placementBatchId?: string;

  // --- Identity (authoritative) ---
  /** Verification state of the identity axis (Requirement 1.2). */
  identityStatus: IdentityStatus;
  /** Present ONLY when `identityStatus === 'IDENTIFIED'`. */
  identity?: CardIdentity;
  /**
   * Non-authoritative operator work-in-progress saved from Batch Identification.
   * It may prefill a later review session but never changes identityStatus,
   * search indexes, grade, placement, or image disposition.
   */
  /** Durable research and provenance for an explicitly confirmed engineer import. */
  importedIdentification?: import('./batch-transfer').BatchIdentificationImportProvenance;
  /** Latest five authoritative import changes; durable bounded card-level audit. */
  identificationImportAudit?: {
    importId: string;
    reviewedAt: string;
    reviewedBy: string;
    before?: CardIdentity;
    after: CardIdentity;
    previousSource?: CardChecklistSourceLineage;
    previousManualOverride?: CardManualIdentityOverride;
  }[];
  identityReviewDraft?: {
    /** External research imported as a non-authoritative reviewer draft. */
    importedFrom?: import('./batch-transfer').BatchIdentificationImportProvenance;
    identity: CardIdentity;
    /** Draft provenance never overwrites the verified identity's lineage. */
    checklistSourceSelection?: ChecklistSourceSelection | null;
    manualIdentityOverrideReason?: string;
    manualIdentityOverrideSourceEvidence?: ChecklistManualSourceEvidence;
    /** Work-in-progress raw/slab classification; independent from vision grade. */
    cardPresentation?: CardPresentation;
    updatedAt: string;
    updatedBy: string;
  };
  /**
   * Durable, non-authoritative checklist chosen for this card in Batch
   * Identification. It overrides the Storage_Batch default for Review only and
   * never changes authoritative identity or the batch's checklist context.
   */
  reviewChecklistSelection?: ReviewChecklistSelection;
  /**
   * Exact imported roster row and optional parallel definition associated with
   * this card. Additive and non-authoritative unless `status === 'VERIFIED'`.
   */
  checklistSourceLineage?: CardChecklistSourceLineage;
  /**
   * Sparse exact-card-and-treatment lookup key derived from source lineage.
   * This ignores mutable taxonomy labels and snapshot fingerprints.
   * It is also written for resolver suggestions (`checklistSourceLineage.status` SUGGESTED/DRAFT, often on
   * UNIDENTIFIED Cards), so readers that need a confirmed link must check `status === 'VERIFIED'`.
   */
  checklistComparablePk?: string;
  /**
   * Explicit fallback provenance for reviewer-entered identities that have no
   * source-backed checklist record. This never substitutes for checklist lineage.
   */
  manualIdentityOverride?: CardManualIdentityOverride;
  /**
   * Sparse manual-review report index: DRAFT when a nested manual review draft
   * exists, otherwise the authoritative manualIdentityOverride status.
   * The draft does not replace the authoritative override audit.
   */
  manualIdentityOverrideStatus?: CardManualIdentityOverride['status'];

  /** Reviewer-confirmed raw/slab classification; absent on legacy cards. */
  cardPresentation?: CardPresentation;

  /** Latest immutable bulk checklist re-verification evidence; prior receipts remain durable. */
  checklistReverificationEvidence?: import('./bulk-checklist-reverification').CardChecklistReverificationEvidence;

  // --- Grade (authoritative) ---
  /** Verification state of the grade axis (Requirement 1.3). */
  gradeStatus: GradeStatus;
  /** Present ONLY when `gradeStatus === 'VERIFIED_GRADE'`. */
  grade?: number;
  /** Derived from `grade`; present ONLY when `gradeStatus === 'VERIFIED_GRADE'`. */
  gradeLabel?: GradeLabel;
  /** Present ONLY when `gradeStatus === 'VERIFIED_GRADE'`. */
  subScores?: SubScores;
  /** Present ONLY when `gradeStatus === 'VERIFIED_GRADE'`. */
  defects?: readonly Defect[];

  /**
   * Operator image-QA state. Device-uploaded cards start at `PENDING`; only
   * `ACCEPTED` cards are eligible for external-service SKU write-back. A
   * `REJECTED` card remains preserved in the Image Issues worklist until an
   * operator re-images or otherwise resolves it. This does not imply
   * identity/grade authority.
   */
  imageQualityStatus?: ImageQualityStatus;
  imageQualityReviewedAt?: string;
  imageQualityReviewedBy?: string;
  imageQualityRejectedReason?: string;
  /**
   * Optional image-only review metadata. A later identity review remains
   * authoritative and may accept or reject the images independently.
   */
  preliminaryImageReview?: PreliminaryImageReview;
  /** Device-captured replacement pair awaiting reviewer/admin approval. */
  pendingImageReplacement?: PendingImageReplacement;

  // --- Non-authoritative ML suggestion (Requirement 2) ---
  /** Retained ML identification + grading output; never authoritative. */
  suggestion?: CardSuggestion;
  /**
   * Optional, non-authoritative observations supplied during device capture.
   * Review may use these to preselect controls, but they never set verified
   * identity fields on their own.
   */
  captureHints?: CardCaptureHints;
  /**
   * Non-authoritative identity imported from the external listing Service_CSV
   * (`csv-identity-import`). Like the ML `suggestion`, it is a hint a human MAY
   * accept or correct during optional Review — it pre-fills the identity form
   * and is NEVER the card's authoritative identity. Distinct from `suggestion`
   * (the ML output) so both sources can coexist. Additive; never re-keys or
   * affects `identity`/`identityStatus`/grade/placement.
   */
  csvSuggestion?: CardIdentity;

  /**
   * Additive, NON-AUTHORITATIVE machine-read OCR text (card-ocr-metadata). Set
   * asynchronously after the card is created by the OCR_Processor; absent until
   * OCR completes and on cards created before this feature (no backfill). A
   * sibling to `suggestion` / `csvSuggestion` — never the authoritative identity
   * or grade, never re-keys, and never affects identityStatus/gradeStatus,
   * placement, or image attributes (Requirement 3.4, 3.5, 4.2, 4.3, 9.2).
   */
  ocr?: OcrMetadata;

  /** Cached, non-authoritative visual evidence used by deterministic checklist matching. */
  checklistImageEvidence?: ChecklistImageEvidence;

  /**
   * Additive, NON-AUTHORITATIVE identity suggestion derived from the card's
   * machine-read OCR text (legacy ocr-identity-extraction). Existing values stay
   * readable for compatibility, but the OCR_Processor no longer creates new
   * values: OCR now supports a separate card-level checklist product hint. A
   * sibling to the ML `suggestion` and `csvSuggestion` — never the authoritative
   * identity or grade, never re-keys, and never affects identityStatus/
   * gradeStatus, placement, or image attributes.
   *
   * MAY be a PARTIAL CardIdentity: any of the four otherwise-required fields
   * (`category`, `set`, `number`, `name`) may be omitted when not derived
   * (Requirement 1.1). Absent optional fields are omitted, never `''`
   * (Requirement 1.4).
   */
  ocrSuggestion?: CardIdentity;

  /**
   * Additive, NON-AUTHORITATIVE identity suggestion sourced from a checklist
   * reference match (checklist-reference). Written by the Checklist_Resolver at
   * Review time ONLY when exactly one Checklist_Entry matches the card's
   * `(product, number)`; absent otherwise and on cards never resolved (no
   * backfill). A fourth sibling to `suggestion`/`csvSuggestion`/`ocrSuggestion`
   * — never the authoritative identity or grade, never re-keys, and never
   * affects identityStatus/gradeStatus, placement, or image attributes
   * (Req 2.1-2.7, 9.1-9.6). MAY carry any subset (none..all) of the optional
   * `CardIdentity` fields, including `serialTo`; absent fields are omitted,
   * never `''` or null (Req 2.5).
   */
  checklistSuggestion?: CardIdentity;

  /**
   * Additive, NON-AUTHORITATIVE imported-checklist product hint derived from
   * this card's bounded front/back OCR and trusted CSV product metadata. It can
   * preselect the card-level checklist resolver during Review, but never writes
   * or implies authoritative identity, grade, placement, image approval, or
   * batch context.
   */
  checklistProductSuggestion?: import('./checklist').ChecklistContextSuggestion;
  /** Bounded reason the latest deterministic product resolution did not match. */
  checklistProductSuggestionFailure?: import('./checklist').ChecklistProductSuggestionFailure;

  // --- Image references ---
  /**
   * Side-labeled image references (two-sided-card-record Requirements 1.2,
   * 2.1, 2.2). Present on all cards created on/after the two-sided-card-record
   * feature. At most one front and one back (Requirement 2.4).
   */
  images?: CardImages;
  /**
   * LEGACY single-image reference (pre-two-sided cards, two-sided-card-record
   * Requirement 5.3). Retained for backward compatibility only. New code reads
   * images via `normalizeCardImages`, which treats a present `imageRef` as
   * `{ front }`. Writers on/after the two-sided feature set `images` instead.
   */
  imageRef?: string;
  /**
   * Earlier image files per side, oldest first. A replacement never deletes the old object; it is recorded here
   * so the original stays referenced (auto-orientation 2026-10-20; manual replacements too).
   */
  imageHistory?: CardImageHistory;
  /** ISO 8601 timestamp. */
  createdAt: string;
  /** Cognito sub of the operator that confirmed the record, or a system actor. */
  createdBy: string;
  source: CardRecordSource;
  /** How the record was created: device Upload vs operator confirm (Requirement 1.5). */
  origin: CardRecordOrigin;
  /** `intakeSessionId` when source is `browser`, `captureBatchId` when source is `device`. */
  sourceRef: string;
  /** Minimal immutable provenance for a card created from an external listing. */
  marketplaceSource?: {
    marketplace: 'EBAY_US';
    externalListingId: string;
    providerImageUrls: string[];
    title?: string;
  };
  /**
   * The operator-initiated Capture_Run this card was uploaded under (UUID v4),
   * stamped at device ingest. `null` for Single_Frame_Captures outside a run;
   * absent on browser-intake cards and on device cards created before this field
   * existed (no backfill). Lets the card-centric Review Queue group cards by run
   * (`card-centric-review-queue`). Mirrors `SessionImage.runId` / the batch's
   * `runId`. Additive — never re-keys or affects identity/grade/placement.
   */
  runId?: string | null;
  /**
   * Mirror of the card's Capture_Batch dismiss-state, set when that batch is
   * dismissed and removed (`REMOVE`) on restore (card-centric-review-queue). Lets
   * the card-centric Review Queue read dismiss-state in a single `tcvs-cards`
   * query (`identityStatus = UNIDENTIFIED && reviewDismissed != true`) without a
   * per-card batch lookup. The authoritative dismiss-state still lives on the
   * `Capture_Batch` (`DISMISSED` + `Prior_Review_State`, capture-batch-dismissal);
   * this is a derived read-optimization. Absent ⇒ not dismissed. Additive — never
   * re-keys or affects identity/grade/placement/image attributes.
   */
  reviewDismissed?: boolean;
}

// ---------------------------------------------------------------------------
// Intake_Session (table: tcvs-sessions)
// ---------------------------------------------------------------------------

/**
 * One captured image inside an Intake_Session or Capture_Batch. Mirrors the
 * `images` list element shape in design.md > Data Models.
 */
export interface SessionImage {
  imageId: string;
  s3Key: string;
  identification: IdentificationResponse;
  grading: GradingResponse;
  confirmed: boolean;
  /**
   * Set when the operator overrode the predicted identity. The corrected
   * identity is what gets written to the resulting Card_Record (Requirement 6.2).
   */
  correctedIdentity?: CardIdentity;
  /**
   * Presigned S3 GET URL for the image, minted by the API on detail
   * reads (`GET /batches/{id}`, `GET /cards/{id}`). Always expires
   * within 15 minutes (Requirement 7.3, Property 20). Absent on
   * persistence reads where no presigning happened.
   */
  imageUrl?: string;
  /**
   * UUID v4 of the operator-initiated Capture_Run this image was
   * uploaded under (Requirement 14, run lifecycle). `null` for
   * Single_Frame_Captures fired outside a run, undefined for older
   * uploads pre-dating the runId schema. The same value is encoded
   * into the S3 key path (`dt={UTC-date}/run={runId}/...`) so the
   * archive can be browsed by run without hitting DynamoDB.
   */
  runId?: string | null;
}

/** An operator-driven intake session. Mirrors the `tcvs-sessions` table. */
export interface IntakeSession {
  sessionId: string;
  state: IntakeSessionState;
  /** Cognito sub of the operator that owns the session. */
  operatorId: string;
  images: readonly SessionImage[];
  /** ISO 8601 timestamp. */
  createdAt: string;
  /** ISO 8601 timestamp. */
  updatedAt: string;
  /**
   * Epoch seconds. Set to `createdAt + 24h` while in non-terminal states; cleared
   * on `CONFIRMED`. Drives the 24-hour image deletion for canceled sessions
   * (Requirement 2.6).
   */
  ttl?: number;
}

// ---------------------------------------------------------------------------
// Capture_Batch (table: tcvs-batches)
// ---------------------------------------------------------------------------

/** Capture_Batch lifecycle state. Mirrors the `state` column in `tcvs-batches`. */
export type CaptureBatchState =
  | 'AWAITING_REVIEW'
  | 'PARTIALLY_CONFIRMED'
  | 'CONFIRMED'
  | 'CANCELED'
  | 'DISMISSED';

/**
 * The review state a Capture_Batch held immediately before dismissal — the
 * Prior_Review_State to which Restore returns the batch (capture-batch-dismissal
 * Requirements 2.1, 3.1, 7.3). Constrained to the two legal review states so it
 * can never record `CONFIRMED`/`CANCELED`/`DISMISSED`.
 */
export type PriorReviewState = 'AWAITING_REVIEW' | 'PARTIALLY_CONFIRMED';

/**
 * A device-initiated batch of one or more captured images. Mirrors the
 * `tcvs-batches` table in design.md > Data Models.
 *
 * `batchId` is `{deviceId}#{submissionTimestamp}` (Requirement 12.10).
 * Capture_Batch images are never auto-confirmed; an operator must confirm
 * each image in the Web_Frontend before any Card_Record is persisted
 * (Requirement 12.13).
 */
export interface CaptureBatch {
  batchId: string;
  deviceId: string;
  state: CaptureBatchState;
  images: readonly SessionImage[];
  /** ISO 8601 timestamp. */
  submittedAt: string;
  /** Set if the device referenced an existing Intake_Session at submit time. */
  linkedSessionId: string | null;
  /**
   * UUID v4 of the operator-initiated Capture_Run this batch was
   * uploaded under (Requirement 14, run lifecycle). `null` for
   * legacy submissions and Single_Frame_Captures fired outside a
   * run; populated for every gantry batch produced after the
   * Capture_Run feature shipped. Operators querying "show me all
   * batches in run X" filter on this field.
   */
  runId?: string | null;
  /**
   * The deterministic `cardId` of the `Card_Record` this batch's submission
   * produced at device Upload time (physical-first-card-tracking: one
   * submission → one card via the Submission_Group_Key). Stamped on the batch
   * row by the Device Ingest Lambda so a batch delete can remove exactly the
   * card it produced without re-deriving the id from the stored images. Absent
   * on legacy rows created before this field existed (the deleter falls back to
   * re-deriving the id in that case).
   */
  cardId?: string;
  /**
   * Set to the Prior_Review_State when the batch is dismissed; read on restore
   * to return the batch to that exact state; removed (`REMOVE`) on restore
   * (capture-batch-dismissal Requirements 2.1, 3.1). Present ONLY while
   * `state === 'DISMISSED'`.
   */
  dismissedFrom?: PriorReviewState;
  /**
   * The human-friendly short code of the `Card_Record` this batch produced.
   * NOT a persisted batch attribute — a read-time enrichment the `GET /batches`
   * list resolves (BatchGet on `cardId` → `shortCode`) for the Review Queue row
   * label, like `SessionImage.imageUrl`. Absent when the batch has no `cardId`
   * (legacy), the card has no short code, or the lookup was skipped/failed.
   */
  shortCode?: string;
}

// ---------------------------------------------------------------------------
// Bulk batch dismiss / restore transition shapes
// ---------------------------------------------------------------------------

/**
 * Request body for the bulk dismiss / restore routes (`POST /batches/dismiss`,
 * `POST /batches/restore`). Carries the set of Capture_Batch identifiers to
 * transition (capture-batch-dismissal Requirement 4.3).
 */
export interface BulkBatchTransitionRequest {
  batchIds: string[];
}

/**
 * Per-batch outcome status for a bulk dismiss / restore operation
 * (capture-batch-dismissal Requirement 4.3). `DISMISSED`/`RESTORED` mark an
 * applied transition, `NOOP` an idempotent already-in-target batch, `NOT_FOUND`
 * an absent batch row, and `CONFLICT` a batch in a non-transitionable state.
 */
export type BatchTransitionStatus = 'DISMISSED' | 'RESTORED' | 'NOOP' | 'NOT_FOUND' | 'CONFLICT';

/**
 * The outcome reported for a single requested batch id in a bulk dismiss /
 * restore response (capture-batch-dismissal Requirement 4.3). `state` carries
 * the resulting Capture_Batch state when known.
 */
export interface BatchTransitionOutcome {
  batchId: string;
  status: BatchTransitionStatus;
  state?: CaptureBatchState;
}

/**
 * Response body for the bulk dismiss / restore routes: exactly one outcome per
 * requested batch id (capture-batch-dismissal Requirement 4.3).
 */
export interface BulkBatchTransitionResponse {
  outcomes: BatchTransitionOutcome[];
}

// ---------------------------------------------------------------------------
// Device registry entry (table: tcvs-devices)
// ---------------------------------------------------------------------------

export type DeviceStatus = 'ACTIVE' | 'REVOKED';

/** Optional human-readable device metadata. */
export interface DeviceMetadata {
  name?: string;
  location?: string;
}

/**
 * A row in the device registry. Mirrors the `tcvs-devices` table in
 * design.md > Data Models. The private key material is never stored
 * server-side; it is returned exactly once in the `POST /devices` response
 * (Requirement 12.2).
 */
export interface DeviceRegistryEntry {
  deviceId: string;
  iotThingName: string;
  certificateId: string;
  certificateArn: string;
  status: DeviceStatus;
  /** ISO 8601 timestamp. */
  issuedAt: string;
  /** ISO 8601 timestamp; null while the device is active. */
  revokedAt: string | null;
  metadata?: DeviceMetadata;
}

// ---------------------------------------------------------------------------
// Training_Example (table: tcvs-training)
// ---------------------------------------------------------------------------

/** Source workflow that produced a Training_Example. */
export type TrainingExampleSource = 'intake' | 'catalog';

/**
 * A labeled training example produced by an operator correction. Mirrors the
 * `tcvs-training` table in design.md > Data Models.
 *
 * `predictedIdentity` is null when the original identification was `unknown`
 * (Requirement 3.2). `ttl` is always >= `createdAt + 365 days` (Requirement 6.4).
 */
export interface TrainingExample {
  exampleId: string;
  /** S3 key for the image being labeled. */
  imageRef: string;
  predictedIdentity: CardIdentity | null;
  correctedIdentity: CardIdentity;
  /** Cognito sub of the operator that supplied the correction. */
  operatorId: string;
  source: TrainingExampleSource;
  /** ISO 8601 timestamp. */
  createdAt: string;
  /** Epoch seconds; always >= `createdAt + 365 days`. */
  ttl: number;
}

// ---------------------------------------------------------------------------
// Card image normalization helper
// ---------------------------------------------------------------------------

/**
 * Normalize a Card_Record's image references to a side-keyed map, handling the
 * legacy single-image `imageRef` shape (two-sided-card-record Requirements 2.5,
 * 5.3, 5.5). Resolution order:
 *
 * 1. If `images` is present, return it (already side-keyed).
 * 2. Else if the legacy `imageRef` is present, return `{ front: imageRef }`
 *    (a legacy single-image card reads as a one-sided front card).
 * 3. Else return `{}`.
 *
 * Pure: never mutates its argument and performs no I/O.
 */
export function normalizeCardImages(card: Pick<CardRecord, 'images' | 'imageRef'>): CardImages {
  if (card.images !== undefined) return card.images;
  if (card.imageRef !== undefined) return { front: card.imageRef };
  return {};
}
